/* Remove any whitespace character (including spaces, tabs, and line breaks) */
export const removeWhitespaces = (str: string): string => str.replace(/\s/g, "");

/**
 * Removes protocol part from the given url string
 * @param urlLike
 * @example ```
 *  removeProtocolFromUrl("https://google.com") // returns "google.com"
 *  removeProtocolFromUrl("mailto:google.com") // returns "google.com"
 * ```
 */
export function removeProtocolFromUrlLikeString(urlLike: string) {
    return urlLike.replace(/^\/\/|^.*?:(\/\/)?/, "");
}

export const testForNonNumericString = (str: string): boolean => {
    return /^[a-zA-Z]$/.test(str);
};

export const testForNumericString = (str: string): boolean => {
    return /^\d+(\/\d+)?(\/\d+)?$/.test(str);
};

/**
 * Adds `.singular` (and `.plural` if 2nd argument is `true`) suffix to given `i18nKey` string
 * based on given `numberOfItems`
 * @param i18nKey
 * @param withPluralSuffix
 */
export function createPluralHelperForI18nKey(i18nKey: string, withPluralSuffix = false) {
    return function getSingularOrPluralKey(numberOfItems: number) {
        if (numberOfItems === 1) {
            return `${i18nKey}.singular`;
        }

        if (withPluralSuffix) {
            return `${i18nKey}.plural`;
        }

        return i18nKey;
    };
}

export const toKebabCase = (string: string) => string.replace(/\s+/g, "-").toLowerCase();

export const locateAllSubstringMatches = (
    str: string,
    searchTerm: string,
    indexes: number[] = [],
    fromIndex = 0,
): number[] => {
    if (searchTerm === "") {
        return [];
    }

    const haystack = str.toLowerCase();
    const needle = searchTerm.toLowerCase();
    const result = [...indexes];

    for (let index = haystack.indexOf(needle, fromIndex); index !== -1; index = haystack.indexOf(needle, index + 1)) {
        result.push(index);
    }

    return result;
};

/* Split a string into segments that are either matches (highlighted) or not */
export const segmentStringBySearchTerm = (
    str: string,
    searchTerm: string,
): readonly { text: string; highlighted: boolean }[] => {
    // Every label in a list re-segments on each render, and the overwhelmingly common case is an
    // empty term. Walking the string to conclude "no matches" is pure waste.
    if (searchTerm === "") {
        return str === "" ? [] : [{ text: str, highlighted: false }];
    }

    const matchStartIndexes = new Set(locateAllSubstringMatches(str, searchTerm));
    const matchEndIndexes = new Set([...matchStartIndexes].map((i) => i + searchTerm.length - 1));
    let buffer = "";

    // `Array.from` iterates code points while the indexes above are UTF-16 offsets. They disagree on
    // astral-plane input; kept as-is because changing it would change the output.
    return Array.from(str).reduce<{ highlighted: boolean; text: string }[]>((result, char, index, arr) => {
        if (matchStartIndexes.has(index) && buffer !== "") {
            result.push({ text: buffer, highlighted: false });
            buffer = "";
        }

        buffer += char;

        if (matchEndIndexes.has(index)) {
            result.push({ text: buffer, highlighted: true });
            buffer = "";
        }

        if (index === arr.length - 1 && buffer !== "") {
            result.push({ text: buffer, highlighted: false });
        }

        return result;
    }, []);
};

/**
 * Truncates the given text to a specified maximum length without cutting off words.
 * If the text is shorter than or equal to the maximum length, it returns the text as is.
 * Otherwise, it truncates the text at the last full word that fits within the maximum length.
 *
 * @param text - The input text to be truncated.
 * @param maxLength - The maximum length of the truncated text. Default is 70.
 * @returns The truncated text.
 */

export const truncateTextByWord = (text: string, maxLength = 70) => {
    const trimmedText = text.trim();
    if (trimmedText.length <= maxLength) {
        return trimmedText;
    }

    const words = trimmedText.split(/\s+/);

    if (words[0].length >= maxLength) {
        return words[0].slice(0, maxLength);
    }

    let truncated = words[0];
    for (let i = 1; i < words.length; i++) {
        const nextWord = words[i];

        if (truncated.length + nextWord.length > maxLength) {
            break;
        }

        truncated += " " + nextWord;
    }

    return truncated;
};

export const replaceUnderscore = (word: string) => word.replace(/_/g, " ");
